import { Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { CreateMessageDto, MessageCourseDto, MessageDto, UpdateMessageDto } from '../../../../../models/dto/message.dto';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafSelectOption } from '../../../../../components/enfaf-select/enfaf-select.component';
import { MessageSendCondition } from '../../../../../models/enums/message_send_condition.enum';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { Course } from '../../../../../models/interfaces/course.interface';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { MessageCourseMode } from '../../../../../models/enums/message_course_modes.enum';
import { KommoService } from '../../../../../core/services/kommo/kommo.service';
import { kommoBotAsyncValidator } from '../../validators/kommo-bot-async.validator';

@Component({
  selector: 'app-modal-message-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    EnfafModalComponent,
    EnfafInputComponent,
    EnfafSelectLightComponent,
    MultiAutocompleteLightComponent,
    EnfafIconComponent
  ],
  templateUrl: './modal-message-form.component.html',
  styleUrl: './modal-message-form.component.scss'
})
export class ModalMessageFormComponent implements OnInit, OnDestroy {
  @ViewChild('courses') coursesAutocomplete?: MultiAutocompleteLightComponent;

  private destroy$ = new Subject<void>();
  private apiService = inject(ApiService);
  private toastService = inject(ToastService);
  private alertService = inject(AlertService);
  private dialogRef = inject(MatDialogRef<ModalMessageFormComponent>);
  private cdr = inject(ChangeDetectorRef);
  private kommoService = inject(KommoService);
  private coursesCache: Course[] = [];
  private coursesFetchPromise: Promise<any> | null = null;
  private syncInterval?: any;
  private isComponentDestroyed = false;

  data: { messageId?: string } = inject(MAT_DIALOG_DATA);

  isSavingMessage = false;
  messageForm!: FormGroup;
  message?: MessageDto;
  selectedCourses: SelectOption[] = [];
  sendConditionOptions: EnfafSelectOption[] = Object.values(MessageSendCondition).map((condition) => ({
    viewValue: condition,
    value: condition
  }));
  messageCourseModes: EnfafSelectOption[] = Object.values(MessageCourseMode).map((condition) => ({
    viewValue: condition,
    value: condition
  }));

  get conditionOffsetLabel(): string {
    if (this.isSendConditionFlash) {
      return 'Horas';
    }
    if (this.isSendConditionInactivity) {
      return 'Días';
    }
    return '';
  }

  get isSendConditionFlash(): boolean {
    return this.messageForm.controls.sendCondition.value === MessageSendCondition.OnFlashCourseDeadlineWarning;
  }

  get isSendConditionInactivity(): boolean {
    return this.messageForm.controls.sendCondition.value === MessageSendCondition.OnUserInactivity;
  }

  get isSendConditionFlashOrInactivity(): boolean {
    return this.isSendConditionFlash || this.isSendConditionInactivity;
  }

  get isSendConditionClassCompletion(): boolean {
    return this.messageForm.controls.sendCondition.value === MessageSendCondition.OnCourseClassCompletion;
  }

  get isAllCoursesMode(): boolean {
    return this.messageForm.controls.mode.value === MessageCourseMode.AllCourses;
  }

  get kommoBotIdErrorLabel(): string {
    const control = this.messageForm.get('kommoBotId');
    if (!control) return '';
    if (control.hasError('botInvalid')) return 'Bot no encontrado en Kommo';
    if (control.hasError('botValidationError')) return 'Error de conexión con Kommo';
    return 'ID de bot requerido';
  }

  constructor() {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadMessageData();
    // subscribe to sendCondition changes to reset offset when needed
    this.messageForm
      .get('sendCondition')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((newCondition) => {
        if (newCondition === MessageSendCondition.OnFlashCourseDeadlineWarning) {
          this.messageForm.get('offset')?.setValue(48); // Default to 48 hours
        } else if (newCondition === MessageSendCondition.OnUserInactivity) {
          this.messageForm.get('offset')?.setValue(7); // Default to 7 days
        } else {
          this.messageForm.get('offset')?.setValue(1); // Reset offset for other conditions
        }
        if (newCondition === MessageSendCondition.OnCourseClassCompletion) {
          this.messageForm.get('mode')?.setValue(MessageCourseMode.AllCourses);
          this.messageForm.get('mode')?.disable();
          this.messageForm.get('mode')?.updateValueAndValidity();
        } else {
          this.messageForm.get('mode')?.enable();
        }
      });
    this.messageForm
      .get('mode')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((newMode) => {
        const lastMode = this.message?.mode || MessageCourseMode.AllCourses;
        if (newMode !== lastMode) {
          this.selectedCourses = [];
          if (this.coursesAutocomplete) {
            this.coursesAutocomplete.selected = [];
          }
        }
      });
    this.messageForm
      .get('offset')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        if (v < 1 || v === null || v === '') {
          this.messageForm.get('offset')?.setValue(1);
        }
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }

  get shouldDisableConfirmButton(): boolean {
    return this.messageForm.invalid || this.isSavingMessage;
  }

  async saveMessage(): Promise<void> {
    if (this.messageForm.invalid) return;

    try {
      this.isSavingMessage = true;

      const formData = this.messageForm.getRawValue();

      if (this.data?.messageId) {
        const body: UpdateMessageDto = {
          ...formData,
          courseIds: this.selectedCourses.map((c) => c.id)
        };
        await firstValueFrom(this.apiService.updateMessage(this.data.messageId, body));
        this.toastService.presentSuccessToast('El mensaje se ha guardado correctamente');
      } else {
        const body: CreateMessageDto = {
          ...formData,
          courseIds: this.selectedCourses.map((c) => c.id)
        };
        await firstValueFrom(this.apiService.createMessage(body));
        this.toastService.presentSuccessToast('El mensaje se ha guardado correctamente');
      }

      this.dialogRef.close(true);
    } catch (error: any) {
      const errorMessage = error?.error?.message || 'Error al guardar el mensaje';
      this.toastService.presentErrorToast(errorMessage);
    } finally {
      this.isSavingMessage = false;
    }
  }

  async confirmCloseModal(): Promise<void> {
    if (this.messageForm.dirty) {
      const shouldClose = await this.alertService.presentConfirmActionAlert(
        'Cerrar ventana',
        '¿Estás seguro(a) de cerrar esta ventana? Los cambios hechos se perderán.',
        'No cerrar',
        'Cerrar'
      );

      if (shouldClose) {
        this.dialogRef.close(false);
      }
    } else {
      this.dialogRef.close(false);
    }
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      this.getCourses()
        .then((courses) => {
          this.processCachedCourses(courses, search, page, pageSize, observer);
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.error(error);
        });
    });
  };

  handleSelectedCourses(selected: SelectOption[]): void {
    this.selectedCourses = selected;
    this.cdr.detectChanges();
  }

  private async getCourses(): Promise<Course[]> {
    if (this.coursesCache.length > 0) {
      return this.coursesCache;
    }

    if (this.coursesFetchPromise) {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses || response || [];
      return this.coursesCache;
    }

    this.coursesFetchPromise = firstValueFrom(this.apiService.fetchAllCourses());

    try {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses || response || [];
      this.coursesFetchPromise = null;
      return this.coursesCache;
    } catch (error) {
      this.coursesFetchPromise = null;
      throw error;
    }
  }

  private processCachedCourses(courses: Course[], search: string, page: number, pageSize: number, observer: any) {
    const filteredCourses = search ? courses.filter((course) => course.name.toLowerCase().includes(search.toLowerCase())) : courses;

    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredCourses.length);
    const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

    const mappedData: SelectOption[] = paginatedCourses.map((course: Course) => ({
      id: course.courseId,
      title: course.name,
      value: course.courseId
    }));

    observer.next({
      data: mappedData,
      total: filteredCourses.length
    });
    observer.complete();
  }

  private initializeForm(): void {
    this.messageForm = new FormGroup({
      title: new FormControl(null, [Validators.required]),
      kommoBotId: new FormControl(null, {
        validators: [Validators.required],
        asyncValidators: [kommoBotAsyncValidator(this.kommoService)],
        updateOn: 'blur'
      }),
      sendCondition: new FormControl(null, [Validators.required]),
      offset: new FormControl(1, [Validators.required]),
      mode: new FormControl(MessageCourseMode.AllCourses, [Validators.required])
    });
  }

  private loadMessageData(): void {
    if (this.data.messageId) {
      this.apiService
        .getMessageById(this.data.messageId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.message = response.data;
              this.messageForm.patchValue({
                title: this.message!.title,
                kommoBotId: this.message!.kommoBotId,
                sendCondition: this.message!.sendCondition,
                offset: this.message!.offset,
                mode: this.message!.mode
              });
            } else {
              this.toastService.presentErrorToast('No se pudo cargar el mensaje');
              this.dialogRef.close(false);
            }
          },
          error: (error) => {
            const errorMessage = error?.error?.message || 'Error al cargar el mensaje';
            this.toastService.presentErrorToast(errorMessage);
            this.dialogRef.close(false);
          }
        });
      this.apiService
        .getMessageCoursesById(this.data.messageId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.setSelectedCourses(response.data);
            }
          },
          error: (error) => {
            const errorMessage = error?.error?.message || 'Error al cargar los cursos del mensaje';
            this.toastService.presentErrorToast(errorMessage);
          }
        });
    } else {
      // Reset form for new message
      this.messageForm.patchValue({});
    }
  }

  removeCourse(course: SelectOption): void {
    if (this.coursesAutocomplete) {
      this.coursesAutocomplete.removeSelected(course);
      this.selectedCourses = this.coursesAutocomplete.selected;
    }
  }

  setSelectedCourses(courses: MessageCourseDto[]) {
    const options: SelectOption[] = courses.map((course) => ({
      id: course.courseId.toString(),
      title: course.name || 'Curso sin nombre',
      value: course.courseId.toString()
    }));

    this.selectedCourses = options;

    if (this.coursesAutocomplete) {
      // En lugar de setSelected, simplemente asignamos al array selected
      this.coursesAutocomplete.selected = [...options];
    }
  }
}
